import { Big } from "big.js";
import { useInView } from "framer-motion";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { KeyedMutator } from "swr";

import Close from "@/assets/icons/close.svg?react";
import Internet from "@/assets/icons/internet.svg?react";
import Mark from "@/assets/icons/mark.svg?react";
import X from "@/assets/icons/x.svg?react";
import { ModalType } from "@/components/Modals/Warning";
import { AIRDROP_LINKS } from "@/constants";
import { useAirdrops, useStore } from "@/hooks/store";
import { useTokens } from "@/hooks/store/useTokens";
import { AirdropProps } from "@/interfaces/store";
import { useModalContext } from "@/providers/modal-provider/context";
import { retrieveAirdrop } from "@/services/api/airdrop";
import { AirdropResponse } from "@/services/rpc/near";
import { displayAmount, formatMonetaryAmount, formatTokenAmount } from "@/utils";

import { NFT } from "./NFT";
import { StatusBadge } from "./StatusBadge";
import styles from "./styles";

const INITIAL_VISIBLE_COUNT = 3;
const PRE_ACTIVE_INTERVAL = 1000 * 5;

export const Airdrop = (airdrop: AirdropProps & { mutateAirdrops: KeyedMutator<AirdropResponse[] | undefined> }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.6 });
  const modal = useModalContext();

  const updateAirdropsById = useAirdrops((store) => store.setAirdropsById);
  const hapiBalance = useAirdrops((store) => store.hapiBalance);
  const { network, currentScore, user } = useStore((store) => ({
    network: store.network,
    currentScore: store.user.networksData[store.network].score,
    user: store.user,
  }));
  const getTokenById = useTokens((store) => store.getTokenById);

  const [claiming, setClaiming] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [itemHeight, setItemHeight] = useState<number>(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const token = getTokenById(network, airdrop.token_id);

  useEffect(() => {
    if (listRef.current?.children.length) {
      const firstItem = listRef.current.children[0] as HTMLElement;
      setItemHeight(firstItem.getBoundingClientRect().height);
    }
  }, []);

  const fulfilledNFTs = airdrop.nfts?.filter((nft) => nft.status === "fulfilled") || [];
  const availableToClaim = Big(airdrop.total_amount).minus(airdrop.claimed_amount);
  const availableNFTsToClaim = Math.min(
    fulfilledNFTs.length,
    Math.trunc(availableToClaim.div(airdrop.claim_amount_by_person).toNumber()),
  );
  const isExpiredScore = currentScore.expirationDate ? Date.now() > currentScore.expirationDate : false;
  const isAllowedScore = currentScore.value >= airdrop.minAllowedScore && !isExpiredScore;

  useEffect(() => {
    const shouldUpdateAirdrop = (airdrop.status === "preActive" || airdrop.status === "active") && inView;
    console.log(shouldUpdateAirdrop, airdrop.id, airdrop.status, inView);
    if (shouldUpdateAirdrop) {
      const intervalId = setInterval(async () => {
        if (airdrop.status === "preActive") {
          updateAirdropsById(network, airdrop);
        } else {
          try {
            const response = await retrieveAirdrop(network, airdrop.id, user.nearAccount);
            console.log(response);
            if (!response) throw Error("Oops");
            updateAirdropsById(network, response);
          } catch (error) {
            console.error(`Error, while loading airdrop(${airdrop.id}) ${network} \n\n`, error);
          }
        }
      }, PRE_ACTIVE_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [airdrop, inView, network, updateAirdropsById, user.nearAccount, user.networksData.NEAR.nft?.array]);

  const isHapiEnoughCondition = Big(hapiBalance ?? "0").gte(airdrop.required_token_balance);

  if (!token) return null;
  const links = AIRDROP_LINKS[token.contractId];

  const totalAmount = displayAmount(airdrop.total_amount, token.metadata.decimals);
  const claimedAmount = displayAmount(airdrop.claimed_amount, token.metadata.decimals);

  const nfts = airdrop.nfts || [];
  const nftHeight = nfts.length === 1 ? itemHeight : itemHeight * Math.min(INITIAL_VISIBLE_COUNT, nfts.length) - 12;

  return (
    <styles.Container ref={ref}>
      <styles.Conditionals style={{ gap: airdrop.isHAPIBalanceRequired ? "0.75rem" : 0 }}>
        <div onClick={!isAllowedScore ? () => modal.warningModal.openModal(ModalType.MintScore) : () => {}}>
          <div>
            <p>{t("Airdrop.Conditionals.ConditionValue", { value: airdrop.minAllowedScore })}</p>
            <styles.NFT.Circle $status={isAllowedScore ? "fulfilled" : "rejected"}>
              {isAllowedScore ? <Mark /> : <Close />}
            </styles.NFT.Circle>
          </div>

          <p>{t("Airdrop.Conditionals.TrustScore")}</p>
        </div>
        {airdrop.holdTimeInDays !== 0 && (
          <div>
            <div>
              <p>{t("Airdrop.Conditionals.ConditionValue", { value: airdrop.holdTimeInDays })}</p>
              <styles.NFT.Circle $status={fulfilledNFTs.length ? "fulfilled" : "rejected"}>
                {fulfilledNFTs.length ? <Mark /> : <Close />}
              </styles.NFT.Circle>
            </div>
            <p>{t("Airdrop.Conditionals.HeldDuration")}</p>
          </div>
        )}
        <div
          onClick={
            !isHapiEnoughCondition
              ? () =>
                  modal.warningModal.openModal(ModalType.BuyHapi, {
                    hapiBalance: displayAmount(
                      Big(airdrop.required_token_balance)
                        .minus(Big(hapiBalance ?? "0").mod(airdrop.required_token_balance ?? "1"))
                        .toFixed(),
                      18,
                    ),
                    requiredBalance: displayAmount(airdrop.required_token_balance, 18),
                  })
              : () => {}
          }
        >
          <div>
            <p>
              {t("Airdrop.Conditionals.ConditionValue", { value: formatTokenAmount(airdrop.required_token_balance) })}
            </p>

            <styles.NFT.Circle $status={isHapiEnoughCondition ? "fulfilled" : "rejected"}>
              {isHapiEnoughCondition ? <Mark /> : <Close />}
            </styles.NFT.Circle>
          </div>
          <p>{t("Airdrop.Conditionals.HAPIBalance")}</p>
        </div>
      </styles.Conditionals>

      <styles.Airdrop $status={airdrop.status}>
        <styles.Row>
          <styles.BaseInfo>
            <StatusBadge status={airdrop.status} />
            <p>{airdrop.name}</p>
            <p>{airdrop.description}</p>

            <styles.Socials>
              <a href={links?.x} target="_blank" rel="noreferrer">
                <X />
              </a>
              <a href={links?.global} target="_blank" rel="noreferrer">
                <Internet />
              </a>
            </styles.Socials>
          </styles.BaseInfo>

          <styles.Image>
            <img src={token.metadata.icon} alt={token.metadata.symbol} />
          </styles.Image>
        </styles.Row>

        {airdrop.status === "active" && (
          <>
            <styles.ClaimedContainer>
              <div>
                <p>{t("Airdrop.Claimed")}</p>
                <p>{`${formatMonetaryAmount(claimedAmount)}/${formatMonetaryAmount(totalAmount)}`}</p>
              </div>
              <styles.Progress max={totalAmount} value={Big(claimedAmount).eq("0") ? undefined : claimedAmount} />
            </styles.ClaimedContainer>
          </>
        )}

        <styles.NFT.List
          ref={listRef}
          initial={{ height: nftHeight }}
          animate={{ height: isExpanded ? "auto" : nftHeight }}
          exit={{ height: nftHeight }}
          transition={{ duration: 0.3, height: { duration: 0.3 } }}
          $isClosed={airdrop.status === "closed"}
        >
          {nfts.map((nft, index) => (
            <NFT
              key={nft.nftId}
              claiming={claiming}
              setClaiming={setClaiming}
              fulfilledNFTs={fulfilledNFTs}
              airdrop={airdrop}
              {...nft}
              availableNFTsToClaim={availableNFTsToClaim}
              currentNftId={index + 1}
              tokenSymbol={token.metadata.symbol}
              claimedCount={airdrop.userClaimsCount}
              claimByPerson={displayAmount(airdrop.claim_amount_by_person, token.metadata.decimals)}
            />
          ))}
        </styles.NFT.List>
        {nfts.length > INITIAL_VISIBLE_COUNT && (
          <styles.ShowMore
            onClick={() => {
              setIsExpanded(!isExpanded);
              if (isExpanded)
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
            }}
          >
            {isExpanded ? t("Actions.ShowLess") : t("Actions.ShowMore")}
          </styles.ShowMore>
        )}
      </styles.Airdrop>
    </styles.Container>
  );
};
